import { buffer } from "@turf/buffer";

export const MAX_POLYGON_EXPANSION_METERS = 1000;
export const POLYGON_EXPANSION_STEP_METERS = 50;

export function clampPolygonExpansion(value) {
  const meters = Number(value);
  return Number.isFinite(meters)
    ? Math.min(MAX_POLYGON_EXPANSION_METERS, Math.max(0, meters))
    : 0;
}

// Always buffer the original paths, never the previous expansion. This keeps
// the distance uniform along the boundary and makes zero an exact reset.
export function expandPolygonPaths(paths, value) {
  const meters = clampPolygonExpansion(value);
  if (!meters || !paths.length) return paths;

  return paths.flatMap((path) => {
    const ring = path.map(({ lat, lng }) => {
      const point = [Number(lng), Number(lat)];
      if (!point.every(Number.isFinite) || Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90) {
        throw new Error("Invalid polygon coordinates");
      }
      return point;
    });
    if (new Set(ring.map((point) => point.join(","))).size < 3) {
      throw new Error("A polygon needs at least three distinct vertices");
    }
    const first = ring[0], last = ring[ring.length - 1];
    const area = ring.reduce((sum, point, index) => {
      const next = ring[(index + 1) % ring.length];
      return sum + (point[0] - first[0]) * (next[1] - first[1])
        - (next[0] - first[0]) * (point[1] - first[1]);
    }, 0);
    if (Math.abs(area) < 1e-12) throw new Error("The polygon has no area");
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);

    const result = buffer(
      { type: "Polygon", coordinates: [ring] },
      meters,
      { units: "meters", steps: 12 },
    );
    const geometry = result?.geometry;
    const polygons = geometry?.type === "Polygon"
      ? [geometry.coordinates]
      : geometry?.type === "MultiPolygon" ? geometry.coordinates : [];
    if (!polygons.length) throw new Error("The polygon could not be expanded");

    return polygons.map((rings) => {
      // The search API accepts filled outlines. Reject an expansion that creates
      // holes instead of silently including an area outside the requested buffer.
      if (rings.length !== 1) throw new Error("This expansion creates an interior boundary");
      return rings[0].map(([lng, lat]) => ({ lat, lng }));
    });
  });
}
